import {
  useContext,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { getActiveLocaleSelector, getIsLoggedInSelector } from '@americanexpress/travel-selectors';
import { useIntl } from 'react-intl';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import { useErrorOnNotOkFetchye } from '@americanexpress/fetchye-amex';
import { ignoreHeadersByKey } from 'fetchye';
import { v4 } from 'uuid';
import { FLIGHT_KEY, HOTEL_KEY } from '../constants/categoryConstants';
import { getFormattedRecentSearchData, isFlightOrPackageLOB } from '../utils/suggestionsUtils';
import {
  getBookingLocationAPIUrl,
  getRecentSearchAPIUrl,
  getTulipsSmartFillAPIUrl,
} from '../selectors/apiSelectors';
import locationContext from '../context/locationsContext';
import {
  CLIENT_ID, LXP_RESULTS_BY_SEARCH_TYPE, LXP_URL_BY_SEARCH_TYPE, V2_LXP_APP_ID,
} from '../constants/apiConstants';
import { hasRecentSearchAirDisplayRule, hasRecentSearchHotelDisplayRule } from '../constants/featureRulesConstants';
import { MAX_SEARCHES_TO_SHOW } from '../constants/recentSearchConstants';
import { generateUUID } from '../utils/generateUuidUtils';
import { moduleName } from '../constants';
import { filterByLobAndTripType, isValidDirectionForFlight } from '../utils/recentSearchUtils';
import { useDebounce } from './useDebounce';
import { sanitizeQuery } from '../utils/sanitizeQueryUtil';

export const useTulipsSmartFillAPI = () => {
  const [debounce, setDebounce] = useState(true);
  const smartFillApiUrl = useSelector(getTulipsSmartFillAPIUrl);
  const locale = useSelector(getActiveLocaleSelector);
  const {
    state: {
      location: {
        query: rawQuery,
        isDefault,
      },
    },
    config: { lineOfBusiness, minimumQueryLength },
  } = useContext(locationContext);
  const query = sanitizeQuery(rawQuery);
  const type = isFlightOrPackageLOB(lineOfBusiness) ? FLIGHT_KEY : lineOfBusiness;
  const reporting = {
    collectionMethod: 'tulips smartFillSuggestions fail', moduleId: moduleName, componentName: 'Suggestions', locale, query,
  };

  useDebounce(setDebounce, query);

  const { isLoading, data, error } = useErrorOnNotOkFetchye(
    `${smartFillApiUrl}?locale=${locale}&query=${query}&type=${type}`,
    { defer: debounce || (!query || query?.length < minimumQueryLength || isDefault) },
    { expectedStatuses: [200], reporting }
  );

  useEffect(() => {
    setDebounce(true);
  }, [data]);

  return {
    suggestions: data?.body?.locations || [],
    isLoading,
    error: error || (data?.status !== 200),
  };
};

export const locationsAPIReporting = ({ key, options, error }) => ({
  moduleID: moduleName,
  component: 'useLocationsSmartFillAPI',
  fetch: {
    url: key,
    status: error?.status,
    correlationId: options?.headers?.['Correlation-ID'],
    activeLocale: options?.headers?.Locale,
  },
});

const locationsDataMapper = (obj) => {
  const {
    locationId, locationName, locationType, ...other
  } = obj;
  return {
    ...other,
    id: locationId,
    name: locationName,
    label: locationName,
    type: locationType,
  };
};

export const useLocationsSmartFillAPI = () => {
  const [debounce, setDebounce] = useState(true);
  const bookingLocationAPIBaseUrl = useSelector(getBookingLocationAPIUrl);
  const activeLocale = useSelector(getActiveLocaleSelector);
  const {
    state: { location: { query: rawQuery, isDefault } },
    config: { lineOfBusiness, minimumQueryLength },
  } = useContext(locationContext);
  const query = sanitizeQuery(rawQuery);

  useDebounce(setDebounce, query);

  const { isLoading, data, error } = useErrorOnNotOkFetchye(
    `${bookingLocationAPIBaseUrl}/${lineOfBusiness}/locations`,
    {
      defer: debounce || (!query || query?.length < minimumQueryLength || isDefault),
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Correlation-ID': v4(),
        Locale: activeLocale,
      },
      mapOptionsToKey: ignoreHeadersByKey(['Correlation-ID']),
      body: JSON.stringify({ keyword: query }),
    },
    { expectedStatuses: [200], reporting: locationsAPIReporting }
  );

  return {
    suggestions: data?.body?.locations.map((obj) => locationsDataMapper(obj)) || [],
    isLoading,
    error: error || (data?.status !== 200),
  };
};
/* istanbul ignore next */
export const useLxpSmartFillAPI = () => {
  const [debounce, setDebounce] = useState(true);
  const {
    state: { location: { query: rawQuery, isDefault } },
    config: { lineOfBusiness, minimumQueryLength },
  } = useContext(locationContext);
  const query = sanitizeQuery(rawQuery);
  const locale = useSelector(getActiveLocaleSelector);
  const smartfillEndpoint = useSelector(LXP_URL_BY_SEARCH_TYPE[lineOfBusiness]);
  const urlWithParameters = `${smartfillEndpoint}?query=${query || ''}`;
  const appId = V2_LXP_APP_ID[locale];
  const reporting = {
    moduleId: moduleName, componentName: 'SmartFillApiHook', locale, appId, smartfillEndpoint, query,
  };

  const existingLxpApiSessionId = sessionStorage.getItem('lxp-api-session-id');

  const savedSessionId = existingLxpApiSessionId
    ? JSON.parse(existingLxpApiSessionId).sessionId : null;
  const savedTimestamp = existingLxpApiSessionId
    ? JSON.parse(existingLxpApiSessionId).expiresAt : null;

  const now = new Date().getTime();

  const savedTimeStampInUnix = new Date(savedTimestamp).getTime();

  const isExpired = savedTimestamp && now >= savedTimeStampInUnix;

  useDebounce(setDebounce, query);

  const { isLoading, data, error } = useErrorOnNotOkFetchye(urlWithParameters,
    {
      defer: debounce || (!query || query?.length < minimumQueryLength || isDefault),
      method: 'GET',
      mapOptionsToKey: ignoreHeadersByKey(['lxp-api-session-id']),
      headers: {
        'lxp-app-id': appId,
        ...(savedSessionId && savedTimestamp && !isExpired) && { 'lxp-api-session-id': savedSessionId },
      },
    },
    { expectedStatuses: [200], reporting }
  );

  if (data?.status === 200) {
    const lxpApiSessionId = data?.headers?.['lxp-api-session-id'];
    if (
      lxpApiSessionId
      && (!savedSessionId || isExpired)
    ) {
      const twentyFourHoursFromNow = new Date().getTime() + (24 * 60 * 60 * 1000);
      sessionStorage.setItem('lxp-api-session-id', JSON.stringify({
        sessionId: lxpApiSessionId,
        expiresAt: new Date(twentyFourHoursFromNow).toUTCString(),
      }));
    }
  }

  useEffect(() => {
    setDebounce(true);
  }, [data]);

  return {
    isLoading,
    suggestions: LXP_RESULTS_BY_SEARCH_TYPE[lineOfBusiness](data?.body || []),
    error: error || (data?.status !== 200),
  };
};

const useHasRecentSearches = (lineOfBusiness, direction) => {
  const hasRecentSearchAir = useConditionalFeature(hasRecentSearchAirDisplayRule);
  const hasRecentSearchHotel = useConditionalFeature(hasRecentSearchHotelDisplayRule);
  if (isFlightOrPackageLOB(lineOfBusiness)) {
    return hasRecentSearchAir && isValidDirectionForFlight(direction);
  }
  if (lineOfBusiness === HOTEL_KEY) {
    return hasRecentSearchHotel;
  }
  return false;
};

export const useRecentSearchAPI = () => {
  const recentSearchApiUrl = useSelector(getRecentSearchAPIUrl);
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const locale = useSelector(getActiveLocaleSelector);
  const {
    config: { lineOfBusiness, direction },
  } = useContext(locationContext);
  const hasRecentSearches = useHasRecentSearches(lineOfBusiness, direction);
  const reporting = { moduleId: moduleName, componentName: 'recentSearchAPI' };
  const {
    data,
    run: reRunRecentSearchAPI,
    isLoading: recentSearchLoading,
    error: recentSearchError,
  } = useErrorOnNotOkFetchye(recentSearchApiUrl,
    {
      defer: !(isLoggedIn && hasRecentSearches),
      mapOptionsToKey: ignoreHeadersByKey(['correlation_id']),
      method: 'POST',
      credentials: 'include',
      headers: () => ({
        'Content-Type': 'application/json',
        clientId: CLIENT_ID,
        correlation_id: generateUUID(),
      }),
      body: JSON.stringify({
        context: [{
          code: 'REQUESTED_CONTEXT',
          value: 'RECENTSEARCHES',
        }],
      }),
    },
    { expectedStatuses: [200], reporting }
  );

  const { formatMessage } = useIntl();
  const connector = formatMessage({ id: 'to' });
  const memoizedData = useMemo(() => getFormattedRecentSearchData(
    {
      recentSearches: data?.body?.recentSearches, locale, connector,
    }), [data, locale, connector]);

  return {
    recentSearches: memoizedData,
    trackingId: data?.body?.trackingId,
    reRunRecentSearchAPI,
    isLoading: recentSearchLoading,
    error: recentSearchError,
  };
};

export const useRecentSearchData = () => {
  const {
    config: { lineOfBusiness, direction },
    state: { tripType },
  } = useContext(locationContext);
  const { recentSearches } = useRecentSearchAPI();
  const hasRecentSearches = useHasRecentSearches(lineOfBusiness, direction);
  return useMemo(() => {
    if (!hasRecentSearches) {
      return [];
    }
    const filteredSearches = filterByLobAndTripType({
      recentSearches,
      lineOfBusiness,
      direction,
      tripType,
    });
    return filteredSearches.slice(0, MAX_SEARCHES_TO_SHOW);
  }, [recentSearches, lineOfBusiness, direction, tripType, hasRecentSearches]);
};
